import React from 'react';
import { Check, Star, Crown, ArrowRight, Lock } from 'lucide-react';
import { trackEvent } from '../utils/metaPixel';
import { TrustBadge } from './TrustBadge';
interface PricingCardProps {
  title: string;
  price: string;
  features: string[];
  isPopular?: boolean;
  isPremium?: boolean;
  checkoutUrl: string;
  image: string;
  comboId: string;
}
function PricingCard({
  title,
  price,
  features,
  isPopular,
  isPremium,
  checkoutUrl,
  image,
  comboId
}: PricingCardProps) {
  const handleCheckout = () => {
    trackEvent('InitiateCheckout', {
      value: parseInt(price.replace('.', '')),
      currency: 'ARS',
      content_name: title
    });
    window.open(checkoutUrl, '_blank');
  };
  return <div className={`relative flex flex-col h-full rounded-2xl transition-all duration-300 ${isPopular ? 'bg-[#1a1a24] border-2 border-[#d4a853] shadow-[0_0_30px_rgba(212,168,83,0.15)] scale-100 md:scale-105 z-10' : 'bg-[#1a1a24]/50 border border-white/10 hover:border-white/20'}`} data-id="element-43">
      {isPopular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#d4a853] text-black font-bold text-sm px-4 py-1 rounded-full flex items-center gap-1 shadow-lg" data-id="element-44">
          <Star className="w-3 h-3 fill-black" data-id="element-45" /> Más Vendido
        </div>}
      {isPremium && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-purple-600 text-white font-bold text-sm px-4 py-1 rounded-full flex items-center gap-1 shadow-lg" data-id="element-46">
          <Crown className="w-3 h-3 fill-white" data-id="element-47" /> Premium
        </div>}

      <div className="p-6 flex-1 flex flex-col" data-id="element-48">
        <div className="text-center mb-4" data-id="element-49">
          <h3 className={`text-xl font-bold mb-2 ${isPopular ? 'text-[#d4a853]' : 'text-white'}`} data-id="element-50">
            {title}
          </h3>
        </div>

        <div className="mb-6 flex justify-center" data-id="element-51">
          <img src={image} alt={title} className="h-72 object-contain drop-shadow-xl" data-id="element-52" />
        </div>

        <ul className="space-y-3 mb-6 flex-1" data-id="element-53">
          {features.map((feature, i) => <li key={i} className="flex items-start gap-3 text-sm text-gray-300" data-id="element-54">
              <Check className={`w-4 h-4 mt-0.5 shrink-0 ${isPopular ? 'text-[#d4a853]' : 'text-gray-500'}`} data-id="element-55" />
              <span data-id="element-56">{feature}</span>
            </li>)}
        </ul>

        <div className="flex items-baseline justify-center gap-1 mb-4" data-id="element-57">
          <span className="text-sm text-gray-400" data-id="element-58">$</span>
          <span className={`text-4xl font-bold ${isPopular ? 'text-[#d4a853]' : 'text-white'}`} data-id="element-59">
            {price}
          </span>
          <span className="text-sm text-gray-400" data-id="element-60">ARS</span>
        </div>

        <button onClick={handleCheckout} className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${isPopular ? 'bg-[#d4a853] hover:bg-[#b88e3e] text-black shadow-lg hover:shadow-[#d4a853]/20' : 'bg-white/10 hover:bg-white/20 text-white'}`} data-id="element-61">
          Quiero {title}
          <ArrowRight className="w-4 h-4" data-id="element-62" />
        </button>
      </div>
    </div>;
}
export function CheckoutSection() {
  return <section id="checkout" className="py-24 px-4 bg-[#0f0f14]" data-id="element-63">
      <div className="max-w-7xl mx-auto" data-id="element-64">
        <div className="text-center mb-12" data-id="element-65">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-66">
            Elegí tu nivel de transformación
          </h2>
          <p className="text-gray-400 text-lg" data-id="element-67">
            Descarga inmediata. Acceso de por vida.
          </p>

          <div className="mt-6 inline-flex items-center gap-2 text-red-400 bg-red-400/10 px-4 py-2 rounded-full text-sm font-medium animate-pulse" data-id="element-68">
            <span className="w-2 h-2 rounded-full bg-red-400" data-id="element-69"></span>
            Solo quedan 7 cupos a este precio
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-3 mb-12 py-6 px-4 bg-white/5 rounded-2xl border border-white/10 max-w-md mx-auto" data-id="element-70">
          <div className="flex items-center gap-2 text-white font-medium" data-id="element-71">
            <Lock className="w-5 h-5 text-[#d4a853]" data-id="element-72" />
            <span data-id="element-73">Pago 100% seguro y encriptado</span>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/tnz6iYpF48MPaPePspDhtk/ML.jpg" alt="Mercado Libre - Pago Seguro" className="h-10 object-contain" data-id="element-74" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch pt-8" data-id="element-75">
          <PricingCard title="Combo 1" price="19.990" comboId="combo1" image="https://cdn.magicpatterns.com/uploads/bCxFjWENBo1Nvy9tWqwv2b/ChatGPT_Image_4_feb_2026,_16_11_41.png" checkoutUrl="https://checkout6.rosetadigital.com/cart/50163692700005:1" features={['Psicología del Apego Moderno (Libro)', 'Límites Emocionales (Bonus)', 'Amor Propio Práctico (Bonus)', 'Detox Afectivo (Bonus)', '📄 LIBROS DIGITALES EN FORMATO PDF', '📧 TE LLEGA AUTOMÁTICAMENTE A TU EMAIL']} data-id="element-76" />

          <PricingCard title="Combo 2" price="29.990" comboId="combo2" isPopular={true} image="https://cdn.magicpatterns.com/uploads/t8XHKk4AQNzZNM5KzD5uxm/ChatGPT_Image_4_feb_2026,_14_32_22.png" checkoutUrl="https://checkout6.rosetadigital.com/cart/50163692765541:1" features={['Todo lo del Combo 1', 'Relaciones Conscientes (Libro Extra)', '📄 LIBROS DIGITALES EN FORMATO PDF', '📧 TE LLEGA AUTOMÁTICAMENTE A TU EMAIL']} data-id="element-77" />

          <PricingCard title="Combo 3" price="39.990" comboId="combo3" isPremium={true} image="https://cdn.magicpatterns.com/uploads/jRP2FfCnZ9SNVtt7zmMoW5/ChatGPT_Image_4_feb_2026,_14_14_25.png" checkoutUrl="https://checkout6.rosetadigital.com/cart/50163692863845:1" features={['Todo lo del Combo 1 y 2', 'Mente Fuerte en el Amor (Libro Premium)', '📄 LIBROS DIGITALES EN FORMATO PDF', '📧 TE LLEGA AUTOMÁTICAMENTE A TU EMAIL']} data-id="element-78" />
        </div>

        <div className="mt-12 text-center" data-id="element-79">
          <p className="text-gray-500 text-sm mb-6" data-id="element-80">
            Formato digital (PDF). Acceso inmediato. Acceso de por vida.
          </p>
          <TrustBadge data-id="element-81" />
        </div>
      </div>
    </section>;
}